/**
 * @fileoverview Development Report state hook. Loads the class list once and the
 * curriculum payload on mount (all classes) and whenever the selected class
 * changes. The Standards/Skills toggle only switches which tree is rendered — it
 * does not refetch.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { fetchCurriculumReport, fetchReportClasses } from '../service/developmentReportService'
import { ALL_CLASSES, type CurriculumReport, type ReportClass, type ReportType } from '../types'

interface UseDevelopmentReportReturn {
  classes: ReportClass[]
  report: CurriculumReport | null
  loading: boolean
  error: string | null
  type: ReportType
  classId: string
  setType: (type: ReportType) => void
  setClassId: (id: string) => void
}

const NATURAL_SORT: Intl.CollatorOptions = { numeric: true, sensitivity: 'base' }
const byName = (a: ReportClass, b: ReportClass) => a.name.localeCompare(b.name, undefined, NATURAL_SORT)

export function useDevelopmentReport(): UseDevelopmentReportReturn {
  const [classes, setClasses] = useState<ReportClass[]>([])
  const [report, setReport] = useState<CurriculumReport | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [type, setType] = useState<ReportType>('standards')
  const [classId, setClassIdState] = useState<string>(ALL_CLASSES)

  const mountedRef = useRef(true)
  const requestRef = useRef(0)

  const loadReport = useCallback(async (group: string) => {
    const requestId = ++requestRef.current
    setLoading(true)
    setError(null)
    try {
      const data = await fetchCurriculumReport(group || undefined)
      if (!mountedRef.current || requestId !== requestRef.current) return
      setReport(data)
    } catch (err) {
      if (!mountedRef.current || requestId !== requestRef.current) return
      setError(err instanceof Error ? err.message : 'Failed to load report')
      setReport(null)
    } finally {
      if (mountedRef.current && requestId === requestRef.current) setLoading(false)
    }
  }, [])

  useEffect(() => {
    const init = async () => {
      try {
        const data = await fetchReportClasses()
        if (mountedRef.current) setClasses([...data].sort(byName))
      } catch (err) {
        if (mountedRef.current) setError(err instanceof Error ? err.message : 'Failed to load classes')
      }
    }
    init()
    loadReport(ALL_CLASSES)
    return () => {
      mountedRef.current = false
    }
  }, [loadReport])

  const setClassId = useCallback(
    (id: string) => {
      setClassIdState(id)
      loadReport(id)
    },
    [loadReport],
  )

  return { classes, report, loading, error, type, classId, setType, setClassId }
}
